/**
 * Capture DMA-aligned consent (ad_user_data, ad_personalization) at request
 * level. Each event can override; this is the default that applies when a
 * row doesn't carry its own consent.
 *
 * Matters most for EEA/UK uploads, but Google accepts the field globally.
 * Defaulting to UNSPECIFIED is deliberate — explicit GRANTED requires the
 * user to actually attest, and DENIED would suppress all matching.
 */
import React from 'react';
import { Card } from '@/components/ui/Card';
import type { Consent, ConsentStatus } from '@/services/api/offlineConversions';

interface Props {
  consent: Consent;
  onChange: (consent: Consent) => void;
}

const OPTIONS: { value: ConsentStatus; label: string; description: string }[] = [
  {
    value: 'CONSENT_STATUS_UNSPECIFIED',
    label: 'Unspecified',
    description:
      "Google will treat it as 'not collected' — fine outside the EEA/UK.",
  },
  {
    value: 'CONSENT_GRANTED',
    label: 'Granted',
    description: "I confirm these customers consented to this use of their data.",
  },
  {
    value: 'CONSENT_DENIED',
    label: 'Denied',
    description:
      "Use only if you must record an explicit denial. Will suppress matching.",
  },
];

const ConsentRow: React.FC<{
  title: string;
  value: ConsentStatus;
  onChange: (v: ConsentStatus) => void;
  fieldId: string;
}> = ({ title, value, onChange, fieldId }) => {
  // `fieldset` + `legend` is the canonical a11y pattern for a group of
  // related radio inputs: screen readers announce the legend as the group's
  // label before each radio option. Avoids the broken `<label htmlFor>` →
  // `<div>` association the prior version had.
  const legendId = `${fieldId}-legend`;
  return (
    <fieldset className="border-0 p-0 m-0" aria-labelledby={legendId}>
      <legend id={legendId} className="font-medium text-gray-900 mb-2 p-0">
        {title}
      </legend>
      <div className="grid gap-2">
        {OPTIONS.map((opt) => (
          <label
            key={opt.value}
            className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 ${
              value === opt.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            }`}
          >
            <input
              type="radio"
              name={fieldId}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
              className="mt-1"
            />
            <div>
              <p className="font-medium text-gray-900">{opt.label}</p>
              <p className="text-xs text-gray-500">{opt.description}</p>
            </div>
          </label>
        ))}
      </div>
    </fieldset>
  );
};

export const ConsentStep: React.FC<Props> = ({ consent, onChange }) => (
  <Card className="space-y-6">
    <ConsentRow
      title="Ad user data consent"
      value={consent.ad_user_data}
      fieldId="consent-ad-user-data"
      onChange={(v) => onChange({ ...consent, ad_user_data: v })}
    />
    <ConsentRow
      title="Ad personalization consent"
      value={consent.ad_personalization}
      fieldId="consent-ad-personalization"
      onChange={(v) => onChange({ ...consent, ad_personalization: v })}
    />
  </Card>
);
